import type { Campaign, UseCaseType } from "@type/agent";
import { USE_CASE_OPTIONS, AVAILABLE_VOICES } from "@type/agent";
import { Card, CardContent, CardHeader, CardTitle } from "@component/card";
import { Input } from "@component/input";

interface CampaignConfigProps {
    campaign: Campaign;
    onChange: (updates: Partial<Campaign>) => void;
    disabled?: boolean;
}

const useCaseOptions = Object.entries(USE_CASE_OPTIONS).map(([value, { label }]) => ({
    value,
    label,
}));

export function CampaignConfig({ campaign, onChange, disabled }: CampaignConfigProps) {
    return (
        <div className={disabled ? "space-y-6 pointer-events-none opacity-60" : "space-y-6"}>
            <Card>
                <CardHeader>
                    <CardTitle>Campaign Configuration</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">Campaign Name</label>
                            <Input value={campaign.name} onChange={(e) => onChange({ name: e.target.value })} placeholder="Enter campaign name" />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">Agent Name</label>
                            <Input value={campaign.agentName || ""} onChange={(e) => onChange({ agentName: e.target.value })} placeholder="e.g. Sara, Alex, Jordan" />
                            <p className="text-xs text-gray-500 mt-1">Name the AI agent will use to introduce itself.</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">Use Case</label>
                            <select value={campaign.useCase} onChange={(e) => onChange({ useCase: e.target.value as UseCaseType })} className="w-full h-9 rounded-md border border-gray-200 bg-white px-3 text-sm dark:bg-gray-900 dark:border-gray-700">
                                {useCaseOptions.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                            </select>
                            {campaign.useCase && USE_CASE_OPTIONS[campaign.useCase] && (
                                <p className="text-xs text-gray-500 mt-1">{USE_CASE_OPTIONS[campaign.useCase].description}</p>
                            )}
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">Voice</label>
                            <select
                                value={campaign.voiceId || "en-west-f"}
                                onChange={(e) => onChange({ voiceId: e.target.value })}
                                className="w-full h-9 rounded-md border border-gray-200 bg-white px-3 text-sm dark:bg-gray-900 dark:border-gray-700"
                            >
                                {AVAILABLE_VOICES.map((voice) => (
                                    <option key={voice.id} value={voice.id}>
                                        {voice.name} ({voice.gender === "female" ? "F" : "M"})
                                    </option>
                                ))}
                            </select>
                            <p className="text-xs text-gray-500 mt-1">Voice and language used by the AI agent on calls.</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">Max Duration (seconds)</label>
                            <Input type="number" value={campaign.maxDurationSeconds.toString()} onChange={(e) => onChange({ maxDurationSeconds: parseInt(e.target.value) || 0 })} />
                            <p className="text-xs text-gray-500 mt-1">Maximum call duration before auto-termination.</p>
                        </div>
                    </div>
                </CardContent>
            </Card>

        </div>
    );
}
